import { normalisePhone } from './phone';

export interface RegisterRow {
  line: number;
  fullName: string;
  /** As typed; the server normalises it. */
  phone: string;
  /** Normalised, for the preview — null when it cannot be read. */
  normalised: string | null;
  password?: string;
  error?: string;
}

/**
 * Turns a pasted class register into rows.
 *
 * Teachers paste out of a spreadsheet, so the separator is whatever their
 * spreadsheet used — a tab from Excel, a comma from a CSV export, sometimes a
 * semicolon from a European locale. Guessing per line is more forgiving than
 * demanding one format, and it costs nothing.
 *
 * Parsing happens here rather than on the server so the teacher sees exactly
 * what will be created before anything is, and can fix a typo in the box
 * instead of in a list of errors afterwards.
 */
/**
 * A field somebody meant as a phone number, whether or not it is a valid one.
 *
 * Deliberately generous: mostly digits, with the punctuation people use. A
 * short or malformed number is recognised here so the row can say *why* it was
 * rejected, rather than reporting the far less useful "no phone number on this
 * line" as though the column were missing.
 */
function looksLikeAttemptedPhone(field: string): boolean {
  const digits = field.replace(/\D/g, '');
  if (digits.length < 3) return false;
  // Almost all of it should be digits and phone punctuation.
  return /^\+?[\d\s()\-.]+$/.test(field.trim());
}

function splitLine(line: string): string[] {
  // Quoted fields survive a comma inside a name: "Toshmatov, Jasur".
  if (line.includes('"')) {
    const out: string[] = [];
    let current = '';
    let quoted = false;
    for (const ch of line) {
      if (ch === '"') quoted = !quoted;
      else if (!quoted && (ch === ',' || ch === '\t' || ch === ';')) {
        out.push(current);
        current = '';
      } else current += ch;
    }
    out.push(current);
    return out.map((f) => f.trim());
  }
  const separator = line.includes('\t') ? '\t' : line.includes(';') ? ';' : ',';
  return line.split(separator).map((f) => f.trim());
}

const looksLikeHeader = (fields: string[]) =>
  fields.some((f) => /^(name|full ?name|student|code|name or code)$/i.test(f)) &&
  fields.some((f) => /^(phone|phone ?number|mobile|tel(ephone)?|number|sign-?in( number)?)$/i.test(f));

export function parseRegister(text: string): RegisterRow[] {
  const lines = text.split(/\r?\n/);
  const rows: RegisterRow[] = [];

  lines.forEach((raw, index) => {
    const line = raw.trim();
    if (!line) return;

    const fields = splitLine(line);
    if (index === 0 && looksLikeHeader(fields)) return;

    // Either order is accepted: whichever field looks like a number is the
    // number, so "Jasur, 90 123 45 67" and "90 123 45 67, Jasur" both work.
    const phoneIndex = fields.findIndex(looksLikeAttemptedPhone);
    const phone = phoneIndex >= 0 ? fields[phoneIndex]! : '';
    const rest = fields.filter((_, i) => i !== phoneIndex).filter(Boolean);
    const fullName = rest[0] ?? '';
    const password = rest[1];

    const normalised = normalisePhone(phone);
    const row: RegisterRow = { line: index + 1, fullName, phone, normalised, password };

    // No number is fine: the student signs in with their candidate number.
    if (phone && !normalised) row.error = `"${phone}" is not a sign-in number (6 to 15 digits)`;
    else if (!fullName) row.error = 'no name or code on this line';
    else if (password && password.length < 8) row.error = 'password is shorter than 8 characters';

    rows.push(row);
  });

  return rows;
}

/** Rows safe to send, and rows the teacher needs to fix first. */
export const partitionRegister = (rows: RegisterRow[]) => ({
  ready: rows.filter((r) => !r.error),
  broken: rows.filter((r) => r.error),
});
